import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useCountry } from "@/contexts/country";
import { usePollFetch } from "@/hooks/polls/usePollFetch";
import PollsFilters, { PollFilters } from "@/components/polls/PollsFilters";
import PollsInfiniteList from "@/components/polls/PollsInfiniteList";
import { useFilteredPolls } from "@/hooks/polls/useFilteredPolls";
import { useInfinitePolls } from "@/hooks/polls/useInfinitePolls";
import PollsHeader from "@/components/polls/PollsHeader";
import useCategories from "@/hooks/useCategories";

import ResourceNavigationTabs from "@/components/navigation/ResourceNavigationTabs";
import { useUnifiedNavigation } from "@/hooks/navigation/useUnifiedNavigation";
import BackToTop from "@/components/ui/BackToTop";

const CountrySpecificPolls = () => {
  const { countryCode } = useParams<{ countryCode: string }>();
  const { availableCountries, setCountry, countryName: currentCountryName } = useCountry();
  const { categories } = useCategories();
  const { handleCountryChange } = useUnifiedNavigation();
  
  const [filters, setFilters] = useState<PollFilters>({
    status: 'active',
    country: countryCode,
    constituency: null,
    category: null,
    year: null,
    search: '',
  });
  
  const [sortBy, setSortBy] = useState<{field: string; direction: 'asc' | 'desc'}>({
    field: 'created_at',
    direction: 'desc'
  });
  
  const { polls, isLoading } = usePollFetch({
    includeAllStatuses: true,
    countryCode: countryCode || null,
    limit: 25  // Reduce initial load for better performance
  });

  const { filteredPolls, activeFiltersCount } = useFilteredPolls({
    polls,
    status: filters.status,
    countryCode: filters.country,
    constituencyCode: filters.constituency,
    category: filters.category,
    year: filters.year,
    search: filters.search,
  });
  
  const { 
    displayedItems,
    hasMore,
    isLoading: isLoadingMore,
    loadMore,
    lastItemRef,
    totalItems
  } = useInfinitePolls({ 
    items: filteredPolls,
    itemsPerPage: 10
  });

  // Compute available categories and years dynamically from current polls
  const { availableCategories, availableYears } = useMemo(() => {
    const pollsToAnalyze = polls.length > 0 ? polls : [];
    
    // Extract unique categories from polls that have them
    const categoryIds = new Set<string>();
    pollsToAnalyze.forEach(poll => {
      if (poll.category_id) {
        categoryIds.add(poll.category_id);
      }
    });
    
    // Map category IDs to category objects
    const availableCategories = categories
      .filter(category => categoryIds.has(category.id))
      .map(category => ({ id: category.id, name: category.name }));
    
    // Extract unique years from polls
    const years = new Set<string>();
    pollsToAnalyze.forEach(poll => {
      if (poll.created_at) {
        const year = new Date(poll.created_at).getFullYear().toString();
        years.add(year);
      }
    });
    
    const availableYears = Array.from(years).sort((a, b) => parseInt(b) - parseInt(a));
    
    return { availableCategories, availableYears };
  }, [polls, categories]);

  const handleFilterChange = (newFilters: PollFilters) => {
    setFilters(newFilters);
  };
  
  const handleSortChange = (sort: {field: string, direction: 'asc' | 'desc'}) => {
    setSortBy(sort);
  };

  // Set country in context and update filters when countryCode changes
  useEffect(() => {
    if (countryCode) {
      setCountry(countryCode);
      setFilters(prev => ({ ...prev, country: countryCode }));
    }
  }, [countryCode, setCountry]);

  // Get country name for display
  const getCountryName = () => {
    if (!countryCode) return undefined;
    const country = availableCountries.find(c => c.code === countryCode);
    return country ? country.name : countryCode;
  };

  const countryName = getCountryName();

  useEffect(() => {
    document.title = `Polls - ${countryName || 'Country'} | NationCard™`;
  }, [countryName]);

  
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-20 flex-1">
        <div className="max-w-5xl mx-auto">
          <PollsHeader countryName={countryName} />
          
          <ResourceNavigationTabs
            countryCode={countryCode}
            countryName={countryName}
            showCountrySelector={true}
            availableCountries={availableCountries}
            onCountryChange={handleCountryChange}
          />
          
          <h1 className="text-3xl font-bold mb-8 text-nation-blue dark:text-blue-300">
            {countryName ? `${countryName} Polls` : 'Country Polls'}
          </h1>
          
          <Card>
            <CardHeader>
              <CardTitle>Browse All Polls</CardTitle>
            </CardHeader>
            <CardContent>
              <PollsFilters 
                onFilterChange={handleFilterChange} 
                onSortChange={handleSortChange}
                activeFiltersCount={activeFiltersCount}
                hideCountryFilter={true}
                availableCategories={availableCategories}
                availableYears={availableYears}
              />
              
              {isLoading ? (
                <div className="py-8 text-center">
                  <div className="animate-spin h-8 w-8 border-4 border-nation-blue border-t-transparent rounded-full mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading polls...</p>
                </div>
              ) : (
                <>
                  <PollsInfiniteList 
                    polls={displayedItems} 
                    emptyMessage="No polls found matching your criteria. Try adjusting your filters."
                    hasMore={hasMore}
                    isLoading={isLoadingMore}
                    loadMore={loadMore}
                    lastItemRef={lastItemRef}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CountrySpecificPolls;
